import * as React from "react";
import { styled } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { SalesListItems, MainListItems, StoreManagerListItems, NoAdminListItems, FeatureListItems, APIManagerListItems } from "./ListItems";
import { Route, Routes, useNavigate } from "react-router-dom";
import { Menu, MenuItem, Tooltip, Chip, Modal, Snackbar } from "@mui/material";
import { useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import MyOrders from "../pages/MyOrders";
import Users from "../pages/Users";
import ApiDocs from "../pages/APIDocs";
import Profile from "../pages/Profile";
import Home from "../pages/Home";
import Notifications from "../pages/Notifications";
import useAuth from "../hooks/useAuth";
import Orders from "../pages/Orders";
import PendingOrders from "../pages/PendingOrders";
import NewOrder from "../pages/NewOrder";
import ZipCodeCounts from "../pages/ZipCodeCounts";
import ChangePassword from "../pages/ChangePassword";
import FAQ from "../pages/FAQ";
import ManageAnnouncements from "../pages/ManageAnnouncements";
import LeadsStore from "../pages/LeadsShop";
import SalesDashboard from "../pages/SalesDashboard";
import StoreManager from "../pages/StoreManager";
import MySubscriptions from "../pages/Subscriptions"
import { useCompany } from "../hooks/useCompany";
import { useUser } from "../hooks/useUser";
import LogoutIcon from '@mui/icons-material/Logout';
import ListItemIcon from '@mui/material/ListItemIcon';
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import { useStorefrontManager } from "../hooks/useStorefrontManager";
//import { Storefront } from "../models/StorefrontResponse";
import { getEnvironment } from "../utils/apiService";
import Footer from "../components/Footer";
import OrderDetailsPage from "../pages/OrderDetailsPage";
import { ParseQueryString } from "../utils/apiService";
import ApiIcon from '@mui/icons-material/Api';
import WalletManagement from "../pages/WalletManagement";
import MyWallet from "../pages/MyWallet";
import LeadsVisualizer from "../pages/LeadVisualizer";
import { useWalletBalance } from "../hooks/useWalletBalance";
import { User } from "../models/UsersResponse";
import Calculators from "../pages/Calculators";
import Products from "../pages/Products";
import ListBuilder from "../pages/ListBuilder";
import Customers from "../pages/Customers"
import APILogs from "../pages/APILogs";
import Lookup from "../pages/Lookup";
import LeadEnhancement from "../pages/LeadEnhancement";
import LoginAs from "./LoginAs";
import LoginIcon from '@mui/icons-material/Login';
import CompressIcon from '@mui/icons-material/Compress';
import Suppressions from "../pages/Suppressions";
import Mortgage from "../pages/Mortgage";
import { useLog } from "../hooks/useLogs";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useUploadErrorLog } from "../hooks/useUploadErrorLog";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileUpload from "../pages/FileUpload";
import RadiusSearch from "../pages/RadiusSearch";
import { useTSGUser } from "../contexts/TSGUserContext";
import PhoneDialog from "./PhoneDialog";
import Company from "../pages/company";
import HubspotUTMForm from "./HubspotUTMForm";
//import ConversationalAIImage from "../assets/images/conversation_icon.png";
import Conversation from "../pages/Conversation";
//import { red } from "@mui/material/colors";
import CloseIcon from '@mui/icons-material/Close';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any;

const drawerWidth: number = 240;
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

const DashboardLayout = () => {
  const navigate = useNavigate();
  const { logs, logInfo, logError } = useLog();
  const [open, setOpen] = React.useState(true);
  const [companyID, setCompanyID] = useState("")
  const [userID, setUserID] = useState("")
  const [logMessageError, setLogMessageError] = useState("")
  const [loginAsUser, setLoginAsUser] = useState<User>()
  const [showStorefrontManager, setShowStorefrontManager] = useState(false)
  const [showOrders, setShowOrders] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [isTSGUser, setIsTSGUser] = useState(false)
  const { isAuthenticating, isAuthenticated, signOut, user } = useAuth();
  const [storefrontName, setStorefrontName] = useState("");
  const { storefront, storefrontError } = useStorefrontManager(storefrontName)
  const { company, companyError } = useCompany(companyID);
  const { customer, customerError } = useUser(userID);
  const [walletBalanceRefreshKey, setWalletBalanceRefreshKey] = useState(0);
  const [logoUrl, setLogoUrl] = useState("")
  const [environment, setEnvironment] = useState("prod");
  const [loginButtonDisplay, setLoginButtonDisplay] = useState('inline');
  const [storefrontOverride, setStorefrontOverride] = useState(false);
  const { walletBalance, walletBalanceError } = useWalletBalance(activeUserID(), walletBalanceRefreshKey)
  const { errorLogAcknowledged, errorLogError } = useUploadErrorLog("DashboardLayout", logMessageError, customer?.id || "unknown", logs)
  const { setTSGUser, tsgUser } = useTSGUser();
  const [openPhoneDialog, setOpenPhoneDialog] = useState(false);
  const [utmParams, setUtmParams] = useState<Record<string, string>>({});
  const [submitHSForm, setSubmitHSForm] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [snackbarMsg, setSnackbarMsg] = useState("")

  useEffect(() => {
    const _hsq = window._hsq = window._hsq || [];
    _hsq.push(['setPath', '/' + location.search]);
    _hsq.push(['trackPageView']);

    setEnvironment(getEnvironment())
    detectStorename()

    if (!isAuthenticated) {
      logInfo("User is not authenticated")
      let newRoute = location.pathname
      if (location.pathname == "/") {
        newRoute = "/products/"
      }

      navigate(newRoute, { replace: true });
    }

    // Close the drawer is screen is small
    if (window.innerWidth < 500) {
      setOpen(false)
    }

    // Set the LoginAs user if specified
    const loginAs = localStorage.getItem("loginAs")
    if (loginAs) {
      try {
        setLoginAsUser(JSON.parse(loginAs))
      } catch (e) {
        logError("failed to parse loginAs user")
      }
    }

    // (function () {
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'landing_page_url'];
    const utmParams: Record<string, string> = {};
    utmKeys.forEach(key => {
      const storedValue = localStorage.getItem(key);
      // Store the value in a dictionary
      if (storedValue) {
        utmParams[key] = storedValue;
      }
    });

    // ?utm_campaign=New%20Leads%20Available%20Email%20Campaign&utm_source=email&utm_content=lead_count_update

    const params = new URLSearchParams(window.location.search);

    utmKeys.forEach(key => {
      const value = params.get(key);
      if (value) {
        utmParams[key] = value;
      }
    });

    // If any UTM parameters are found, submit them to HubSpot
    if (Object.keys(utmParams).length > 0) {
      setUtmParams(utmParams);
      console.log("UTM Parameters found:", utmParams);
    }
  }, []);

  useEffect(() => {
    if (!utmParams || Object.keys(utmParams).length === 0) {
      return;
    }

    const timerId = setTimeout(() => {
      setSubmitHSForm(true);
    }, 2000); // 2-second delay

    return () => clearTimeout(timerId); // Cleanup
  }, [utmParams]);

  useEffect(() => {
    const colorMode = localStorage.getItem("colorMode")
    setIsDarkMode(colorMode == "dark")
    logInfo("Dashboard Layout reloaded")
  }, [])

  useEffect(() => {
    logInfo("Log Data Uploaded")
    setLogMessageError("")
  }, [errorLogAcknowledged])

  useEffect(() => {
    if (errorLogError == "") {
      return
    }
    logInfo(`Log Data Upload failed: ${errorLogError}`)
    setLogMessageError("")
  }, [errorLogError])

  useEffect(() => {
    if (customer == undefined || customer.id == undefined || customer.id == "") {

      if (isAuthenticated) {
        logInfo(`User authenticated but not loaded yet...User ID: ${userID}, Username: ${user.username}`)
        setUserID(user.username)
      } else {
        logInfo("Authenticated user not found...not good!!!")
        navigate("/signin");
      }
      return
    }

    logInfo(`User Loaded: ${customer.firstName} ${customer.lastName} (${customer.id})`)

    if (customer.storefrontName != "") {
      localStorage.setItem("storefront", customer.storefrontName)
    }

    if (storefrontName == "") {
      logError("no storefront name...this shouldn't happen")
    }

    const activeSfUser = activeUser()

    // If a TSG user is overriding the storefront then ignore the storefront configured for the user
    if (activeSfUser && activeSfUser.storefrontName == "tsg" && storefrontName != "tsg" && activeSfUser.email.includes("@theshare.group")) {
      logInfo("Storefront Override by TSG user! Storefront=" + storefrontName)
      setStorefrontOverride(true)
      setShowOrders(true)
      setShowStorefrontManager(true)
      return
    }

    if (activeSfUser && customer.companyID != "") {
      setCompanyID(activeSfUser.companyID)
    }


    if (activeSfUser && activeSfUser.storefrontName != "") {
      //logInfo("storefront specified in logged-in as user profile (" + activeSfUser.storefrontName + ")")
      setStorefrontName(activeSfUser.storefrontName)
    } else {
      setStorefrontName(customer.storefrontName)
    }

    if (activeSfUser) {
      setTSGUser(activeSfUser)
      setIsTSGUser(activeSfUser.email.includes("@theshare.group"))
    } else {
      setIsTSGUser(customer.email.includes("@theshare.group"))
    }

  }, [customer]);

  useEffect(() => {

    if (storefront) {
      logInfo("Storefront Loaded: " + storefront.storeName)
      // get the color mode to use
      const colorMode = localStorage.getItem("colorMode")
      if (colorMode == "dark") {
        storefront.activeColors = storefront.darkMode
      } else {
        storefront.activeColors = storefront.lightMode
      }

      //setStorefront(storefronts[0])
    }
  }, [storefront]);

  useEffect(() => {
    getlogoURLs();
  }, [storefront]);

  useEffect(() => {
    if (walletBalanceError) {
      console.log(`user: ${JSON.stringify(user)}`);
      setSnackbarMsg("There was an error retrieving your wallet balance (" + walletBalanceError + "). Please try refreshing the page. If this problem continues, contact support.")
      setOpenSnackbar(true)
    }
  }, [walletBalanceError]);

  useEffect(() => {
    if (customerError) {
      console.log(`user: ${JSON.stringify(user)}`);
      setSnackbarMsg("There was an error retrieving your customer data (" + customerError + "). Please try refreshing the page. If this problem continues, contact support.")
      setOpenSnackbar(true)
    }
  }, [customerError]);

  useEffect(() => {
    if (companyError) {
      console.log(`user: ${JSON.stringify(user)}`);
      setSnackbarMsg("There was an error retrieving your company data (" + companyError + "). Please try refreshing the page. If this problem continues, contact support.")
      setOpenSnackbar(true)
    }
  }, [companyError]);

  useEffect(() => {
    if (storefrontError) {
      console.log(`user: ${JSON.stringify(user)}`);
      setSnackbarMsg("There was an error retrieving your storefront data (" + storefrontError + "). Please try refreshing the page. If this problem continues, contact support.")
      setOpenSnackbar(true)
    }
  }, [storefrontError]);

  useEffect(() => {
    if (company == undefined) {
      return
    }

    logInfo("[Dashboard] Company Loaded: " + company.name)

    // Storefront Availability
    if (company.roles.indexOf('storefront') > -1) {
      setShowStorefrontManager(true)
    } else {
      setShowStorefrontManager(false)
    }

    // View Orders Availability
    if (company.roles.indexOf('orders') > -1) {
      setShowOrders(true)
    } else {
      setShowOrders(false)
    }
  }, [company]);

  useEffect(() => {
    if (isAuthenticated) {
      setLoginButtonDisplay('none')
    } else {
      setLoginButtonDisplay('inline')
    }
  }, [isAuthenticated, user]);

  const toggleDrawer = () => {
    setOpen(!open);
  };
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const currentUserName = () => {
    const cu = activeUser()
    if (cu) {
      return cu.firstName + " " + cu.lastName
    }
  }

  useEffect(() => {
    showPhoneDialogIfNeeded()
  }, [tsgUser]);

  function showPhoneDialogIfNeeded() {
    if (tsgUser) {
      if (tsgUser.phone != "") {
        return
      }

      console.log("User Acknowledgements: ", tsgUser?.acknowledgements)

      let phoneRemind = false;
      for (const ack of tsgUser?.acknowledgements || []) {
        if (ack.name === "phone-remind") {

          // If the acknowledgment date is more than 14 days old, prompt the user again
          const ackDate = new Date(ack.date);
          const currentDate = new Date();
          const diffTime = Math.abs(currentDate.getTime() - ackDate.getTime());
          console.log("AckDate: ", ackDate, "CurrentDate: ", currentDate, "DiffTime: ", diffTime)
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
          if (diffDays > 1) {
            console.log("Phone Remind Acknowledgement is older than 14 days, prompting user...")
            phoneRemind = true;
            break;
          }
        }
      }

      if (phoneRemind) {
        console.log("Phone-Remind Not Found or has expired. Prompting User...")
        setOpenPhoneDialog(true);
      }
    }
  }

  function detectStorename() {
    if (storefrontName == "") {
      let sf = ""
      const queryParams = ParseQueryString(location.search)
      const sfParam = queryParams.get("sf")

      if (sfParam != undefined) {
        logInfo("storefront specified in URL (" + sf + ")")
        sf = sfParam
      }

      if (isAuthenticated) {
        // See if we need to allow an override
        if (user && user.attributes && user.attributes.email) {
          if (sf != "" && sf != "tsg" && (user.attributes.email + "").includes("@theshare.group")) {
            setStorefrontName(sf)
            return
          }
        }
      }

      const activeSfUser = activeUser()
      if (activeSfUser && activeSfUser.storefrontName != "") {
        //logInfo("storefront specified in logged-in as user profile (" + activeSfUser.storefrontName + ")")
        sf = activeSfUser.storefrontName
      }


      if (sf == "") {
        const savedStorefront = localStorage.getItem("storefront")

        if (savedStorefront) {
          sf = savedStorefront
          logInfo("storefront specified in local storage (" + sf + ")")
        }
      }

      if (sf == "") {
        logInfo("storefront has no indicators, defaulting to 'tsg'")
        sf = "tsg"
      }

      setStorefrontName(sf)
    }
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const customerFeatures = (cust?: User): string[] => {
    if (cust && cust.features) {
      return cust.features
    }

    return []
  }

  const handleLogout = async () => {
    try {
      // sign out user
      await signOut();
    } catch (err) {
      logError("Auth Error: " + err)
    }
  };

  const handleChangePassword = async () => {
    navigate("/changepassword");
  };

  const handleApiDocs = async () => {
    navigate("/apidocs");
    handleCloseUserMenu();
  };

  const handleLoginAs = async () => {
    navigate("/loginas");
    handleCloseUserMenu();
  };

  const handleSuppressions = async () => {
    navigate("/suppressions");
    handleCloseUserMenu();
  };

  const handleFileUpload = async () => {
    navigate("/upload");
    handleCloseUserMenu();
  };

  const handleSendLogs = async () => {
    setLogMessageError("[No Error] User-initiated log upload")
    handleCloseUserMenu();
  };

  // const handleStartConversation = async () => {
  //   navigate("/conversation");
  //   handleCloseUserMenu();
  // };

  const handleProfileSettings = () => {
    setAnchorElUser(null);
    navigate("/profile")
  }

  const onDarkModeChanged = () => {
    setAnchorElUser(null);
    const newMode = !isDarkMode
    const colorMode = newMode ? "dark" : "light"

    localStorage.setItem("colorMode", colorMode)
    setIsDarkMode(newMode)
    location.reload();
  };

  function activeUserID(): string {
    if (loginAsUser && loginAsUser.id != "") {
      return loginAsUser.id
    } else {
      return userID
    }
  }

  function activeUser() {
    if (loginAsUser && loginAsUser.id != "") {
      return loginAsUser
    } else {
      return customer
    }
  }

  function AdminListComponent() {
    if (isInSalesGroup() && !storefrontOverride) {
      return <SalesListItems storefront={storefront} features={customerFeatures(activeUser())} isTSG={isTSGUser} />
    }

    return <NoAdminListItems />
  }

  function StoreManagerListComponent() {
    return StoreManagerListItems(showStorefrontManager, showOrders, storefront)
  }

  function APIListComponent() {
    if (company != undefined && company.roles.indexOf('query_api') > -1) {
      return APIManagerListItems(storefront)
    } else {
      return <div></div>
    }
  }

  function FeatureListComponent() {
    return <FeatureListItems storefront={storefront} features={customerFeatures(activeUser())} isTSG={isTSGUser} />
  }

  function isInSalesGroup() {
    try {
      const activeSfUser = activeUser()

      if (activeSfUser && String(activeSfUser.email).includes("@theshare.group")) {
        return true;
      }

    } catch (err: unknown) {
      return false
    }
    return false
  }

  const getlogoURLs = () => {
    if (storefrontName == "") {
      return
    }

    if (storefront.activeColors.logo == "") {
      setLogoUrl("")
    } else {
      if (isDarkMode) {
        if (environment == "prod") {
          setLogoUrl("https://shop.theshare.group/storefront/" + storefront.storeName + "_logo_dark.png")
        } else {
          setLogoUrl("https://" + environment + ".portal.theshare.group/storefront/" + storefront.storeName + "_logo_dark.png")
        }
      } else {
        if (environment == "prod") {
          setLogoUrl("https://shop.theshare.group/storefront/" + storefront.storeName + "_logo_light.png")
        } else {
          setLogoUrl("https://" + environment + ".portal.theshare.group/storefront/" + storefront.storeName + "_logo_light.png")
        }
      }
    }
  }

  const handlePhoneDialogClose = () => setOpenPhoneDialog(false);

  useEffect(() => {
    // if authenticated then track the user for HubSpot
    if (isAuthenticated) {
      let email = ""

      if (user && user.attributes && user.attributes.email) {
        email = user.attributes.email
      } else {
        console.log("user: ", user)
      }
      const _hsq = window._hsq = window._hsq || [];
      _hsq.push(["identify", {
        email: email,
        id: user.username
      }]);

      try {
        const activeSfUser = activeUser()
        if (activeSfUser && activeSfUser.id != "") {
          setUserID(activeSfUser.id)
        } else {
          setUserID(user.username)
        }
      }
      catch (e) {
        logError(`signin error: ${e as string}`)
        navigate("/signin")
      }
    }
  }, [isAuthenticating, isAuthenticated]);

  const handleLogin = () => {
    navigate("/signin");
  }

  const walletBalanceChanged = () => {
    setWalletBalanceRefreshKey(walletBalanceRefreshKey + 1)
  }

  const isSignedInUserTSG = () => {
    if (user && user.attributes && user.attributes.email) {
      return user.attributes.email.includes("@theshare.group")
    }
    return false
  }

  const phoneDialogStyle = {
    position: 'absolute',
    top: '40%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: storefront.activeColors.mainPage.headerBackground,
    border: '1px solid #5c5c5e',
    boxShadow: 24,
    p: 1,
  };

  const hsFormSubmitted = () => {
    setSubmitHSForm(false);

    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'landing_page_url'];

    // Clear UTM parameters from localStorage after submission
    utmKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    console.log("UTM Parameters submitted to HubSpot and cleared from localStorage");
  }

  const handleSnackbarClose = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbar(false);
  };

  const snackbarAction = (
    <React.Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleSnackbarClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {isAuthenticated
        ? <AppBar position="absolute" open={open}>
          <Toolbar
            sx={{
              pr: "24px", // keep right padding when drawer closed
              backgroundColor: storefront.activeColors.mainPage.headerBackground
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{
                marginRight: "36px",
                color: storefront.activeColors.leftMenu.icon,
                ...(open && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>
            {logoUrl == ""
              ? <div />
              : storefront.storeName == "tsg" || storefront.storeName == ""
                ? <a href="https://theshare.group"><img src={logoUrl} height={48} /></a>
                : <img src={logoUrl} height={48} />
            }

            <Typography
              component="h1"
              variant="h6"
              color={storefront.activeColors.mainPage.headerText}
              noWrap
              fontWeight="fontWeightMedium"
              sx={{ flexGrow: 1, padding: '10px' }}
            >
              {storefront?.title}
            </Typography>
            {/*<Button variant="contained" onClick={() => navigate("/leadenhancement")} sx={{ mr: 1, backgroundColor: red[500], color: "white" }} >
              50% off Skip Tracing!
            </Button>*/}
            <Box sx={{ flexGrow: 0, marginRight: -3 }}>

              {/* {customerFeatures(activeUser()).indexOf('ai_agent') > -1 || isSignedInUserTSG() &&  */}
              {/* <Button variant="outlined" onClick={handleStartConversation} sx={{ mr: 1 }} endIcon={<img src={ConversationalAIImage} alt="Chat with an Agent" style={{ height: 30 }} />} >
                <Typography sx={{ color: 'red', fontSize: 16, marginRight: 0.5 }}>Try Me!</Typography>
                <Typography sx={{ color: storefront.activeColors.mainPage.avatarForeground, fontSize: 16, marginRight: 0.5 }}>AI Agent</Typography>
              </Button> */}
              {/* } */}
              {walletBalance && walletBalance.balance && walletBalance.balance > 0
                ? <Chip label={"Wallet Balance: " + walletBalance.balance} onClick={() => navigate('/mywallet')} sx={{ backgroundColor: storefront.activeColors.mainPage.avatarForeground, color: storefront.activeColors.mainPage.pageButtonBackground }}></Chip>
                : <div />
              }
              <Tooltip title="Open settings">
                <IconButton
                  size="medium"
                  onClick={handleOpenUserMenu}
                  sx={{ p: 0, mx: 2 }}
                ><Stack direction="row" sx={{ color: storefront.activeColors.mainPage.avatarForeground }} alignItems={"center"}>
                    {loginAsUser && loginAsUser.id != ""
                      ? <Chip color={"primary"} sx={{ mr: -1, backgroundColor: storefront.activeColors.mainPage.avatarBackground, color: storefront.activeColors.mainPage.headerText, height: 40 }} label={"Logged-in As: " + loginAsUser.firstName + " " + loginAsUser.lastName} onClick={() => { localStorage.setItem("loginAs", ""); location.reload(); }}></Chip>
                      : <Chip color="primary" sx={{ mr: -1, backgroundColor: storefront.activeColors.mainPage.avatarBackground, color: storefront.activeColors.mainPage.headerText, height: 40 }} label={currentUserName()}></Chip>
                    }
                    <Avatar alt="" sx={{ color: storefront.activeColors.mainPage.avatarForeground, backgroundColor: storefront.activeColors.mainPage.avatarBackground }} />
                  </Stack>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem onClick={handleProfileSettings}>
                  <ListItemIcon>
                    <Person2OutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography textAlign="center">Profile Settings</Typography>
                </MenuItem>
                <Divider />
                <MenuItem key="darkMode" onClick={onDarkModeChanged}>
                  <ListItemIcon>
                    {isDarkMode
                      ? <CheckBoxOutlinedIcon />
                      : <CheckBoxOutlineBlankOutlinedIcon />
                    }
                  </ListItemIcon>
                  Dark Mode
                </MenuItem>
                <MenuItem key="change-password" onClick={handleChangePassword}>
                  <ListItemIcon>
                    <LockResetOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography textAlign="center">Change Password</Typography>
                </MenuItem>
                {isTSGUser &&
                  <MenuItem key="loginas" onClick={handleLoginAs}>
                    <ListItemIcon>
                      <LoginIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography textAlign="center">Login As...</Typography>
                  </MenuItem>
                }
                {company != undefined && company.roles.indexOf('query_api') > -1 &&
                  <MenuItem key="api-docs" onClick={handleApiDocs}>
                    <ListItemIcon>
                      <ApiIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography textAlign="center">API Documentation</Typography>
                  </MenuItem>
                }
                <MenuItem key="suppressions" onClick={handleSuppressions}>
                  <ListItemIcon>
                    <CompressIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography textAlign="center">Suppression Files</Typography>
                </MenuItem>
                <MenuItem key="fileupload" onClick={handleFileUpload}>
                  <ListItemIcon>
                    <FileUploadIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography textAlign="center">File Upload</Typography>
                </MenuItem>
                <Divider />
                {/* <MenuItem key="api-docs" onClick={() => {navigate("/apidocs/index.html")}}>
                  <ListItemIcon>
                    <LockResetOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography textAlign="center">Change Password</Typography>
                </MenuItem>
              <Divider /> */}
                <MenuItem key="send-logs" onClick={handleSendLogs}>
                  <ListItemIcon>
                    <CloudUploadIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography textAlign="center">Send Log Data</Typography>
                </MenuItem>
                <Divider />
                <MenuItem key="sign-out" onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography textAlign="center">Log Out</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>
        : <AppBar>
          <Toolbar
            sx={{
              backgroundColor: storefront.activeColors.mainPage.headerBackground,
            }}
          >
            {logoUrl == ""
              ? <div />
              : <a href="https://theshare.group"><img src={logoUrl} height={48} /></a>
            }

            {/* <Stack direction="row" justifyContent="space-evenly" alignItems="flex-end"> */}
            <Typography
              component="h1"
              variant="h5"
              color={storefront.activeColors.mainPage.headerText}
              noWrap
              fontWeight="fontWeightMedium"
              sx={{ flexGrow: 1, padding: '10px' }}
            >
              {storefront?.title}
            </Typography>
            <IconButton onClick={handleLogin} sx={{ color: storefront?.activeColors.mainPage.pageIconBackground, display: loginButtonDisplay, marginRight: -2 }}>
              <Stack direction="row">
                <Typography>Login</Typography>
                <LoginIcon />
              </Stack>
            </IconButton>
            {/* </Stack> */}
          </Toolbar>
        </AppBar>
      }
      {isAuthenticated
        ? <Drawer variant="permanent" open={open} sx={{}}>
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              px: [1],

            }}
          >
            <IconButton type="button" onClick={toggleDrawer} sx={{ color: storefront.activeColors.leftMenu.icon }}>
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider />
          <List component="nav" sx={{}}>
            <MainListItems storefront={storefront} features={customerFeatures(activeUser())} isTSG={isTSGUser} />
            <Divider sx={{ my: 1, backgroundColor: storefront.activeColors.leftMenu.divider }} />
            <StoreManagerListComponent />
            <FeatureListComponent />
            <APIListComponent />
            <AdminListComponent />
          </List>
          <Footer position="absolute" storefrontName={storefront.storeName} />
        </Drawer>
        : <Box>
          <Footer position="absolute" storefrontName={storefront.storeName} />
        </Box>
      }

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
        }}
      >
        <Toolbar />

        <HubspotUTMForm params={utmParams} submit={submitHSForm} onFormSubmitted={hsFormSubmitted} />

        <Routes>
          <Route path="/" element={<LeadsStore storefront={storefront} />} />
          <Route path="/home" element={<Home storefront={storefront} isDarkMode={isDarkMode} />} />
          <Route path="/myorders" element={<MyOrders storefront={storefront} />} />
          <Route path="/users" element={<Users />} />
          <Route path="/profile" element={<Profile storefront={storefront} />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/orders" element={<Orders storefront={storefront} />} />
          <Route path="/pendingorders" element={<PendingOrders />} />
          <Route path="/neworder" element={<NewOrder storefront={storefront} />} />
          <Route path="/zipcodecounts" element={<ZipCodeCounts />} />
          <Route path="/changepassword" element={<ChangePassword />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/announcements" element={<ManageAnnouncements />} />
          <Route path="/leadsstore" element={<LeadsStore storefront={storefront} />} />
          <Route path="/products/:id" element={<LeadsStore storefront={storefront} />} />
          <Route path="/products" element={<LeadsStore storefront={storefront} />} />
          <Route path="/salesdashboard" element={<SalesDashboard />} />
          <Route path="/storefront" element={<StoreManager storefront={storefront} />} />
          <Route path="/mysubscriptions" element={<MySubscriptions storefront={storefront} />} />
          <Route path="/order" element={<OrderDetailsPage />} />
          <Route path="/apidocs" element={<ApiDocs />} />
          <Route path="/leadsvisualizer" element={<LeadsVisualizer storefront={storefront} />} />
          <Route path="/walletmanagement" element={<WalletManagement storefront={storefront} walletBalanceChanged={walletBalanceChanged} />} />
          <Route path="/mywallet" element={<MyWallet storefront={storefront} />} />
          <Route path="/calculators" element={<Calculators />} />
          <Route path="/storeproducts" element={<Products storefront={storefront} />} />
          <Route path="/listbuilder" element={<ListBuilder storefront={storefront} />} />
          <Route path="/customers" element={<Customers storefront={storefront} />} />
          <Route path="/apilogs" element={<APILogs storefront={storefront} />} />
          <Route path="/lookup" element={<Lookup storefront={storefront} />} />
          <Route path="/leadenhancement" element={<LeadEnhancement storefront={storefront} />} />
          <Route path="/loginas" element={<LoginAs />} />
          <Route path="/suppressions" element={<Suppressions storefront={storefront} isTSGUser={isSignedInUserTSG()} />} />
          <Route path="/upload" element={<FileUpload storefront={storefront} />} />
          <Route path="/mortgage" element={<Mortgage storefront={storefront} />} />
          <Route path="/radiussearch" element={<RadiusSearch storefront={storefront} />} />
          <Route path="/companies" element={<Company />} />
          <Route path="/conversation" element={<Conversation storefront={storefront} />} />
        </Routes>
        <Modal
          open={openPhoneDialog}
          onClose={handlePhoneDialogClose}
        >
          <Box sx={phoneDialogStyle}>
            <PhoneDialog closeDialogCallback={handlePhoneDialogClose} storefront={storefront} />
          </Box>
        </Modal>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={30000}
          onClose={handleSnackbarClose}
          message={snackbarMsg}
          action={snackbarAction}
        />
      </Box>
    </Box>
  );
};

export default DashboardLayout;
